import { createApiRef, FetchApi } from '@backstage/core-plugin-api';
import type { WorkspacesApi, WorkspaceInfo, BundleInfo } from './types';

export const workspacesApiRef = createApiRef<WorkspacesApi>({
  id: 'plugin.aws-workspaces.api',
});

export class WorkspacesClient implements WorkspacesApi {
  private readonly fetchApi: FetchApi;

  constructor(options: { fetchApi: FetchApi }) {
    this.fetchApi = options.fetchApi;
  }

  private async fetch<T>(path: string, options?: RequestInit): Promise<T> {
    const baseUrl = '/api/aws-workspaces';
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, options);

    if (!response.ok) {
      const body = await response.text();
      throw new Error(
        `Request failed with status ${response.status}: ${body}`,
      );
    }

    return response.json();
  }

  async listWorkspaces(): Promise<WorkspaceInfo[]> {
    const result = await this.fetch<{ items: WorkspaceInfo[] }>('/workspaces');
    return result.items;
  }

  async listAllWorkspaces(): Promise<WorkspaceInfo[]> {
    const result = await this.fetch<{ items: WorkspaceInfo[] }>(
      '/workspaces/all',
    );
    return result.items;
  }

  async getWorkspace(workspaceId: string): Promise<WorkspaceInfo> {
    return this.fetch<WorkspaceInfo>(`/workspaces/${workspaceId}`);
  }

  async startWorkspace(workspaceId: string): Promise<void> {
    await this.fetch(`/workspaces/${workspaceId}/start`, { method: 'POST' });
  }

  async stopWorkspace(workspaceId: string): Promise<void> {
    await this.fetch(`/workspaces/${workspaceId}/stop`, { method: 'POST' });
  }

  async rebuildWorkspace(workspaceId: string): Promise<void> {
    await this.fetch(`/workspaces/${workspaceId}/rebuild`, { method: 'POST' });
  }

  async terminateWorkspace(workspaceId: string): Promise<void> {
    await this.fetch(`/workspaces/${workspaceId}/terminate`, {
      method: 'POST',
    });
  }

  async listBundles(): Promise<BundleInfo[]> {
    const result = await this.fetch<{ items: BundleInfo[] }>('/bundles');
    return result.items;
  }
}
